import React, { useState } from 'react';
import * as Icon from 'ubs-icons';
import { COLOR } from '../common/constant';
import UButton from "../UButton";
import UPopup from "ubs-framework/v2/UPopup";
import { showToastError, showToastSuccess } from "../common/function";

const UFileDownload = ({ 
    url, 
    fileName, 
    children, 
    type = 'icon', // 'icon' | 'button'
    iconSize = 16, 
    iconColor = COLOR.u_icon_bg,
    className = "",
    onSuccess,
    onError,
    buttonText = "Download",
    showIcon = false, // for button mode to show icon alongside text
    tooltip = null, // text for tooltip
    buttonProps = {},
    iconProps = {},
    popupProps = {},
    spinnerColor,
    spinnerSize,
    successMessage = "Download Successful",
    errorMessage = "Download Failed"
}) => {
    const [loading, setLoading] = useState(false);

    const handleDownload = (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        if (loading || !url) return;

        setLoading(true);

        fetch(url)
            .then(response => {
                 if (!response.ok) throw new Error('Network response was not ok');
                 return response.blob();
            })
            .then(blob => {
                const blobUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = blobUrl;
                // Get extension from URL or blob type if possible, or fallback
                let extension = url.split('.').pop().split(/[#?]/)[0]; 
                if (!extension || extension.length > 4) {
                     extension = ""; 
                }
                
                let finalFileName = fileName || 'document';
                if (extension && !finalFileName.endsWith(`.${extension}`)) {
                    finalFileName = `${finalFileName}.${extension}`;
                }

                link.setAttribute('download', finalFileName);
                document.body.appendChild(link);
                link.click();
                link.parentNode.removeChild(link);
                window.URL.revokeObjectURL(blobUrl);
                setLoading(false);
                showToastSuccess(successMessage);
                if (onSuccess) onSuccess();
            })
            .catch((err) => {
                setLoading(false);
                console.error("Download failed", err);
                // Fallback to simple window open if fetch fails
                window.open(url, '_blank');
                // showToastError(String(err?.message || errorMessage)); // Don't show error if we fallback? Or show warning? 
                // User requirement says "Add proper Error message handling"
                // If fallback opens, maybe not an error in user eyes? 
                // But fetch failed. 
                // Let's stick to user request: "Add proper Error message handling".
                // If we fallback, it might still be worth noting the direct download failed if we consider it an error.
                // However, often fallback is the "fix".
                // But if the URL is bad, window.open will also fail (404 page).
                // Let's assume if fetch fails we try window.open. If that works visualy, good.
                // But typically if fetch fails due to CORS, window.open works.
                // If fetch fails due to 404, window.open shows 404.
                // Let's show a generic error if the user expectation is "download failed" but we opened a tab.
                // Actually, if we fallback to window.open, it's a "soft fail" of the direct download.
                // Let's show error only if we can't do anything or maybe just log it.
                // User asked for "Add proper Error message handling".
                // Let's show the error toast.
                showToastError(errorMessage);
                if (onError) onError(err);
            });
    };

    // Loader for Icon mode
    const Loader = () => (
        <div className="u_loader-spinner small" style={{
            width: spinnerSize || iconSize, 
            height: spinnerSize || iconSize, 
            border: '2px solid #ccc', 
            borderTop: `2px solid ${spinnerColor || iconColor || '#000'}`, 
            borderRadius: '50%', 
            animation: 'spin 1s linear infinite',
            display: 'inline-block',
            verticalAlign: 'middle'
        }}></div>
    );

    const renderTrigger = () => {
         if (children) {
             return (
                <span className={`u_cp ${className}`} onClick={handleDownload} style={{ position: 'relative', display: 'inline-block' }}>
                    {loading ? <Loader /> : children}
                </span>
             );
         }
         
         if (type === 'button') {
              let btnChildren = null;
              if (showIcon) {
                  btnChildren = <span className="u_flex u_align-center"><Icon.Download size={14} fill={COLOR.u_white} className="u_mr-5" /> {buttonText}</span>;
              }

              return (
                  <UButton 
                      variant="blue"
                      {...buttonProps}
                      type={buttonText} 
                      onClick={handleDownload}
                      loading={loading}
                      cn={className}
                  >
                      {btnChildren}
                  </UButton>
              )
         }
         
         // Icon mode
         return (
            <span className={`u_cp ${className}`} onClick={handleDownload}>
                {loading ? <Loader /> : <Icon.Download size={iconSize} fill={iconColor} {...iconProps} />}
            </span>
         )
    }

    if (tooltip) {
         return (
             <UPopup 
                 params={{className: "u_tooltip u_tooltip-full", on: ['hover'], ...popupProps}} 
                 desc={<span className='u_text-c'>{tooltip}</span>}
             >
                {renderTrigger()}
             </UPopup>
         )
    }

    return renderTrigger();
};

export default UFileDownload;
